// src/components/TrainDoorTransition.js
import React from 'react'
import { CITIES } from '../data/content'
import { getRandomQuote } from '../data/cityQuotes'

const CITY_EXTRAS = {
  berlin: { name: 'Berlin', primaryColor: '#F0CA00' },
  madrid: { name: 'Madrid', primaryColor: '#E4032E' },
}

const CITY_AMBIANCE = {
  paris: 3, nyc: 2, london: 2, milan: 1, berlin: 1, madrid: 1,
}

const CITY_AMBIANCE_LABEL = {
  paris: 'Paris Ambiance', nyc: 'New York Ambiance',
  london: 'London Ambiance', milan: 'Milan Ambiance',
  berlin: 'Berlin Ambiance', madrid: 'Madrid Ambiance',
}

// Bump this version number each time you replace audio files on Bluehost
const AUDIO_VERSION = '2'

function getRandomAmbianceUrl(city) {
  const count = CITY_AMBIANCE[city] || 1
  const n = Math.floor(Math.random() * count) + 1
  return `/sounds/door-chime-${city}-${n}.mp3?v=${AUDIO_VERSION}`
}

export default function TrainDoorTransition({ targetCity, onComplete }) {
  const [phase, setPhase] = React.useState('closing')
  const [playing, setPlaying] = React.useState(false)
  const [bufferReady, setBufferReady] = React.useState(false)
  const timersRef = React.useRef([])
  const stopFnRef = React.useRef(null)
  const decodedBufferRef = React.useRef(null)
  const hasAdvancedRef = React.useRef(false)
  const audioCtxRef = React.useRef(null)
  const shouldPlayRef = React.useRef(false)
  const onCompleteRef = React.useRef(onComplete)

  const cityData = CITIES[targetCity] || CITY_EXTRAS[targetCity] || {}
  const color = cityData.primaryColor || '#EE352E'
  const name = cityData.name || targetCity
  const quote = React.useRef(getRandomQuote(targetCity)).current
  const ambianceUrl = React.useRef(getRandomAmbianceUrl(targetCity)).current
  const textColor = color === '#F0CA00' ? 'rgba(0,0,0,0.85)' : '#fff'
  const mutedColor = color === '#F0CA00' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)'

  React.useEffect(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()
    audioCtxRef.current = ctx
    fetch(ambianceUrl)
      .then(r => r.arrayBuffer())
      .then(buf => ctx.decodeAudioData(buf))
      .then(decoded => {
        decodedBufferRef.current = { decoded, ctx }
        setBufferReady(true)
        if (shouldPlayRef.current) { playBuffer(); shouldPlayRef.current = false }
      })
      .catch(() => {})
  }, [ambianceUrl])

  const stopAudio = React.useCallback(() => {
    if (stopFnRef.current) { stopFnRef.current(); stopFnRef.current = null }
    setPlaying(false)
  }, [])

  const doAdvance = React.useCallback(() => {
    if (hasAdvancedRef.current) return
    hasAdvancedRef.current = true
    timersRef.current.forEach(clearTimeout)
    stopAudio()
    setPhase('opening')
    timersRef.current = [setTimeout(() => onCompleteRef.current(), 480)]
  }, [stopAudio])

  React.useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])

  React.useEffect(() => {
    const t1 = setTimeout(() => {
      setPhase('closed')
      shouldPlayRef.current = true
      setTimeout(() => {
        shouldPlayRef.current = false
        if (decodedBufferRef.current && !stopFnRef.current) { playBuffer() }
      }, 2000)
    }, 480)
    const t2 = setTimeout(doAdvance, 30000)
    timersRef.current = [t1, t2]
    return () => { timersRef.current.forEach(clearTimeout); stopAudio() }
  }, []) // eslint-disable-line

  const playBuffer = React.useCallback(async () => {
    if (!decodedBufferRef.current) return
    const { decoded, ctx } = decodedBufferRef.current
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume()
    }
    console.log('[TrainDoor] AudioContext state after resume:', audioCtxRef.current?.state)
    const attemptPlay = (isRetry) => {
      try {
        const gain = ctx.createGain()
        gain.gain.value = 0.7
        gain.connect(ctx.destination)
        const source = ctx.createBufferSource()
        source.buffer = decoded
        source.loop = false
        source.connect(gain)
        try {
          source.start(0)
        } catch(startErr) {
          console.error('[TrainDoor] source.start() failed:', startErr)
          throw startErr
        }
        setPlaying(true)
        stopFnRef.current = () => { try { source.stop() } catch(_) {} }
        source.onended = () => { stopFnRef.current = null; setPlaying(false) }
      } catch(_) {
        if (!isRetry) setTimeout(() => attemptPlay(true), 800)
      }
    }
    attemptPlay(false)
  }, [])

  const handlePlayToggle = React.useCallback((e) => {
    e.stopPropagation()
    if (playing) { stopAudio(); return }
    if (!decodedBufferRef.current) return
    playBuffer()
  }, [playing, stopAudio, playBuffer])

  const doorTransform = (side) => {
    if (phase === 'closing' || phase === 'closed') return 'translateX(0)'
    return side === 'left' ? 'translateX(-100%)' : 'translateX(100%)'
  }
  const transition = phase === 'closing'
    ? 'transform 0.46s cubic-bezier(0.6,0,0.4,1)'
    : 'transform 0.44s cubic-bezier(0.4,0,0.2,1)'

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'all' }} onClick={doAdvance}>

      <div style={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '100%', background: color, transform: doorTransform('left'), transition, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 6 }}>
        <div style={{ width: 5, height: 80, background: 'rgba(0,0,0,0.18)', borderRadius: 3 }} />
      </div>

      <div style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', background: color, transform: doorTransform('right'), transition, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 6 }}>
        <div style={{ width: 5, height: 80, background: 'rgba(0,0,0,0.18)', borderRadius: 3 }} />
      </div>

      {phase === 'closed' && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 36px', fontFamily: 'Helvetica Neue, Arial, sans-serif', animation: 'doorLabelIn 0.22s ease-out forwards' }}>

          <div style={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 10, letterSpacing: 5, color: mutedColor, marginBottom: 8 }}>BOARDING</div>
            <div style={{ fontSize: 30, fontWeight: 900, color: textColor, letterSpacing: -0.5 }}>{name}</div>
            <div style={{ fontSize: 11, color: mutedColor, letterSpacing: 2, marginBottom: quote ? 28 : 0 }}>LINE</div>
            {quote && (
              <div style={{ textAlign: 'center', animation: 'quoteIn 0.5s ease-out 0.3s both', maxWidth: 300 }}>
                <div style={{ width: 24, height: 1, background: color === '#F0CA00' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.25)', margin: '0 auto 18px' }} />
                <p style={{ fontSize: 15, fontStyle: 'italic', fontWeight: 500, color: textColor, lineHeight: 1.55, margin: 0, opacity: 0.9 }}>"{quote.text}"</p>
                {quote.author && <p style={{ fontSize: 10, letterSpacing: 2, color: mutedColor, marginTop: 12, fontWeight: 600 }}>— {quote.author.toUpperCase()}</p>}
              </div>
            )}
          </div>

          <button onClick={handlePlayToggle} style={{
            marginTop: 26, pointerEvents: 'all',
            display: 'flex', alignItems: 'center', gap: 8,
            background: playing ? (color === '#F0CA00' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.22)') : (color === '#F0CA00' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'),
            border: `1px solid ${color === '#F0CA00' ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.22)'}`,
            borderRadius: 24, padding: '9px 18px 9px 10px',
            cursor: bufferReady ? 'pointer' : 'default', fontFamily: 'inherit',
            WebkitTapHighlightColor: 'transparent', outline: 'none', touchAction: 'manipulation',
            opacity: bufferReady ? 1 : 0.4, transition: 'opacity 0.3s',
          }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: color === '#F0CA00' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {playing ? (
                <svg width="10" height="12" viewBox="0 0 10 12" fill={color}><rect x="0" y="0" width="3.5" height="12" rx="1"/><rect x="6.5" y="0" width="3.5" height="12" rx="1"/></svg>
              ) : (
                <svg width="10" height="12" viewBox="0 0 10 12" fill={color}><polygon points="1,0 10,6 1,12"/></svg>
              )}
            </div>
            <span style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 700, color: color === '#F0CA00' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.82)' }}>
              {playing ? 'PLAYING...' : bufferReady ? (CITY_AMBIANCE_LABEL[targetCity] || 'AMBIANCE') : 'LOADING...'}
            </span>
          </button>

          <div style={{ marginTop: 20, pointerEvents: 'none', fontSize: 9, letterSpacing: 3, color: mutedColor, fontWeight: 600, animation: 'tapPulse 1.5s ease-in-out 0.8s infinite' }}>
            TAP TO CONTINUE
          </div>
        </div>
      )}

      <style>{`
        @keyframes doorLabelIn { from{opacity:0;transform:scale(0.94)} to{opacity:1;transform:scale(1)} }
        @keyframes quoteIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes tapPulse { 0%,100%{opacity:.3} 50%{opacity:.7} }
      `}</style>
    </div>
  )
}
